import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ServiceContext } from 'providers/ServiceProvider';
import { Portal } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  LEASING_SERVICE_LINKING_TYPES,
  LEASING_SERVICE_TYPES_SUMMARY,
  LEASING_SERVICE_TYPE_LINKINGS
} from 'features/leasingServiceType/queries';
import { get, sequence, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { concatFailure, items, required, shape, stopOnFirstFailure } from '@totalsoft/pure-validations';
import { Card, IconButton, Typography } from '@totalsoft/rocket-ui';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_LEASING_SERVICE_TYPES_WRITE } from 'constants/rightsCodes';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import LinkingComponent from './LinkingComponent';
import FlexBox from 'components/common/FlexBox';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { LEASING_SERVICE_TYPE_LINKINGS_SAVED } from 'features/leasingServiceType/subscriptions';
import { SAVE_LEASING_SERVICE_TYPE_LINKINGS } from 'features/leasingServiceType/mutations';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { Prompt } from 'react-router-dom';
import { distinctFromField, uniqueProperties } from 'utils/simple-validations';

const validator = items(
  [
    uniqueProperties(['typeId', 'sourceId', 'destinationId'], 'LeasingServiceType.LinkingDuplicated'),
    shape({
      typeId: required,
      sourceId: [required, distinctFromField('destinationId', 'LeasingServiceType.LinkingDestination')] |> stopOnFirstFailure,
      destinationId: [required, distinctFromField('sourceId', 'LeasingServiceType.LinkingSource')] |> stopOnFirstFailure
    })
  ] |> concatFailure
);

const LinkingsComponent = () => {
  const { t } = useTranslation();
  const [activeService] = useContext(ServiceContext);
  const addToast = useToast();

  const canWrite = usePermission(ADMIN_LEASING_SERVICE_TYPES_WRITE);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);
  const [validation, validate, resetValidation] = useDirtyFieldValidation(validator);

  const [initialData, setInitialData] = useState([]);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  // #region Data loading

  const { loading } = useQuery(LEASING_SERVICE_TYPE_LINKINGS, {
    onCompleted: data => {
      const linkings = data?.leasingServiceTypeLinkings || [];
      resetLens(linkings);
      setInitialData(linkings);
    }
  });

  const { data: typesSummaryData } = useQuery(LEASING_SERVICE_TYPES_SUMMARY);

  const { data: linkingTypesData } = useQuery(LEASING_SERVICE_LINKING_TYPES);

  const typesSummary = useMemo(() => typesSummaryData?.leasingServiceTypesSummary || [], [typesSummaryData]);
  const linkingTypes = useMemo(() => linkingTypesData?.leasingServiceLinkingTypes || [], [linkingTypesData]);

  // #endregion

  // #region Handlers

  const handleAdd = () => {
    const newLinking = {
      id: crypto.randomUUID(),
      typeId: null,
      sourceId: null,
      destinationId: null
    };
    const current = lens |> get;
    (lens |> set)(current.concat(newLinking));
  };

  const handleDelete = id => {
    const current = lens |> get;
    (lens |> set)(current.filter(item => item.id !== id));
  };

  const handleUndoChanges = useCallback(() => {
    resetLens(initialData);
    resetValidation();
  }, [resetLens, initialData, resetValidation]);

  // #endregion

  // #region Save

  const [saveLeasingServiceTypeLinkings] = useMutation(SAVE_LEASING_SERVICE_TYPE_LINKINGS, {
    onError: () => {
      setPending(false);
    }
  });

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    setPending(true);

    const sanitizedData = data.map(item => {
      const sanitizedItem = { ...item };
      if (typeof sanitizedItem.id === 'string') sanitizedItem.id = null;
      delete sanitizedItem.typeName;
      delete sanitizedItem.sourceName;
      delete sanitizedItem.destinationName;
      return sanitizedItem;
    });

    saveLeasingServiceTypeLinkings({ variables: { input: sanitizedData } });
  };

  useSubscription(LEASING_SERVICE_TYPE_LINKINGS_SAVED, {
    onData: ({ client, data }) => {
      const newLinkings = data.data.leasingServiceTypeLinkingsSaved.typeLinkings;
      client.writeQuery({
        query: LEASING_SERVICE_TYPE_LINKINGS,
        context: { serviceName: activeService?.name },
        data: { leasingServiceTypeLinkings: newLinkings }
      });
      resetLens(newLinkings);
      setInitialData(newLinkings);
      resetValidation();
      setPending(false);
      addToast(t('LeasingServiceType.LinkingsSaved'), 'success');
    }
  });

  // #endregion

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const disabled = useMemo(() => !canWrite || pending, [canWrite, pending]);

  const hasLinkings = (lens.length |> get) > 0;

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Card icon={MergeTypeIcon}>
        {loading && <WrappedFakeText />}
        {!loading && !hasLinkings && (
          <Typography fontWeight='bold' align='center'>
            {t('LeasingServiceType.NoLinkings')}
          </Typography>
        )}
        {!loading && hasLinkings && (
          <FlexBox direction='column' gap={1.5}>
            {(lens |> sequence).map((linkingLens, index) => (
              <LinkingComponent
                lens={linkingLens}
                typesSummary={typesSummary}
                linkingTypes={linkingTypes}
                onDelete={handleDelete}
                validation={validation[index]}
                disabled={disabled}
                key={linkingLens.id |> get}
              />
            ))}
          </FlexBox>
        )}
      </Card>
      {canWrite && (
        <Portal container={() => document.getElementById('service-linking-actions')}>
          {isDirty && (
            <IconButton color='transparent' tooltip={t('UndoChanges')} onClick={handleUndoChanges} disabled={disabled}>
              <ReplayIcon />
            </IconButton>
          )}
          <IconButton color='transparent' tooltip={t('LeasingServiceType.AddLinking')} onClick={handleAdd} disabled={disabled}>
            <AddIcon />
          </IconButton>
          <IconButton color='transparent' tooltip={t('Save')} onClick={handleSave} disabled={disabled || !isDirty}>
            <SaveIcon />
          </IconButton>
        </Portal>
      )}
    </>
  );
};

LinkingsComponent.propTypes = {};

export default LinkingsComponent;
